import { type GeneratedFile } from 'common/files/files.types'
import { type ProposalStatus, type TenderStatus, type TopicType } from 'common/tenders/tenders.types'
import { type Certificate } from 'common/certificates/certificates.types'
import { type Role } from 'common/utils/types'

import { type LookupItem } from '../lookup/lookup.types'

export enum VendorStatus {
  new = 'new',
  pending = 'pending',
  activated = 'activated',
  additional_documents = 'additional_documents',
  pending_documents = 'pending_documents',
  pendingVerification = 'pending_verification',
  verified = 'verified',
  rejected = 'rejected',
}
export enum VendorAIStatus {
  pending = 'pending',
  approved = 'approved',
  rejected = 'rejected',
}
export enum VendorAIResult {
  aiBeginners = 'Neupućeni u AI',
  aiExperts = 'Upoznati sa AI',
  aiReady = 'Spremni za AI',
  aiCompetent = 'Kompetentni u AI',
}
export enum UserRole {
  Admin = 'admin',
  Stakeholder = 'stakeholder',
  Company = 'company',
  Consultant = 'consultant',
  Vendor = 'vendor',
}

export interface VendorCore {
  id: string
  tin: string
  status: VendorStatus
  logo?: {
    key: string
    filename: string
    read?: {
      url: string
      expiresIn: number
    }
  }
}

export interface VendorInfo {
  name: string
  fullName: string
  cid: string
  aprEmail: string
  creationDate: Date
  city: {
    id: number
    name: string
  }
  address: string
  industry: {
    code: string
    name: string
  }
  profit: {
    amount: number
    size: string
  }
  phones: string[]
  verificationDate?: Date
  verificationRetryDate?: Date
  verificationNote?: string
  user: User
  isConsultant: boolean
  numOfTendersAnsweredRfb: number
  numOfSubmittedTenders: number
  awardedTenders: Array<{
    companyName: string
    price: number
    programName: string
    projectName: string
    solution: {
      category: {
        audience: string
        id: number
        name: string
      }
      id: number
      name: string
      slot: number
    }
    status: string
    tenderName: string
  }>
}

export interface User {
  id: string
  email: string
  name: {
    first: string
    last: string
  }
  role: Role
  isVendor: boolean
  certificates: Certificate[]
}

export interface VendorAdditionalInfo {
  description: string
  contact: {
    name: {
      first: string
      last: string
    }
    role: string
    phone: string
    email: string
  }
  topics: TopicType[]
  referenceDocuments: {
    documents: GeneratedFile[]
  }
  additionalDocuments: {
    documents: GeneratedFile[]
  }
  specialties: LookupItem[]
  industries: LookupItem[]
  aiReferenceDocuments: {
    documents: GeneratedFile[]
  }
  aiStatus: VendorAIStatus
  aiVerificationNote: string
  aiVerificationRetryDate: string
}

export interface VendorCompany extends VendorCore, Partial<VendorInfo> {
  cid: string
  phones: string[]
  website: string
  topics: TopicType[]
  logo?: {
    key: string
    filename: string
    read?: {
      url: string
      expiresIn: number
    }
  }
  contact: {
    name: {
      first: string
      last: string
    }
    role: string
    phone: string
    email: string
  }
  referenceDocuments: {
    documents: GeneratedFile[]
  }
  additionalDocuments: {
    documents: GeneratedFile[]
  }
  specialties: LookupItem[]
  industries: LookupItem[]
  verificationNote: string
}

export type Vendor = VendorCore & Partial<VendorInfo> & Partial<VendorAdditionalInfo> & Partial<VendorCompany>

export enum DecisionStatus {
  satisfactory = 'satisfactory',
  marginal = 'marginal',
  unsatisfactory = 'unsatisfactory',
  unknown = 'unknown',
}

interface DecisionBoundary {
  marginalBoundary?: number | string | boolean
  satisfactoryBoundary: number | string | boolean
}

interface DecisionDetail<T> extends DecisionBoundary {
  value: T
  status: DecisionStatus
}

export interface DecisionDetails {
  id: string
  companyAge: DecisionDetail<string>
  employeeCount: DecisionDetail<number>
  accountBlocked: DecisionDetail<boolean>
  accountBlockedRecently: DecisionDetail<boolean>
  averageMargin: DecisionDetail<number>
  incomeTrend: DecisionDetail<number>
  industry: DecisionDetail<string> & { satisfactoryBoundary: string }
  incomeLastYear: number
}
export enum QuestionType {
  COMPANY_AGE = 'companyAge',
  EMPLOYEE_COUNT = 'employeeCount',
  ACCOUNT_BLOCKED = 'accountBlocked',
  ACCOUNT_BLOCKED_RECENTLY = 'accountBlockedRecently',
  AVERAGE_MARGIN = 'averageMargin',
  INCOME_TREND = 'incomeTrend',
  INDUSTRY = 'industry',
}

export interface GetMyOffer {
  ipp: number
  page: number
}

export interface MyOffer {
  project: {
    id: string
    name: string
  }
  tender: {
    id: string
    name: string
    status: TenderStatus
  }
  proposalStatus: ProposalStatus
  deadlineProposal: Date
  deadlineResolution: Date
}
